import { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { CalendarCheck, ArrowsClockwise, Warning, CheckCircle } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionsBilling() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);

  const fetchPreview = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/subscriptions/billing-preview`, { withCredentials: true });
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar assinaturas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPreview(); }, []);

  const applyBillingDay = async () => {
    setApplying(true);
    try {
      const { data: res } = await axios.post(
        `${API}/admin/subscriptions/set-billing-day`,
        { dry_run: false },
        { withCredentials: true }
      );
      setResult(res);
      setConfirmOpen(false);
      toast.success(`${res.updated} assinatura(s) atualizada(s) para o dia 30`);
      fetchPreview();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao aplicar');
    } finally {
      setApplying(false);
    }
  };

  const subs = data?.subscriptions || [];

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6" data-testid="subscriptions-billing-page">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-['Outfit'] text-2xl font-bold text-[#1A1A1A] flex items-center gap-2">
              <CalendarCheck size={26} className="text-[#E85D04]" /> Dia de Cobrança das Assinaturas
            </h1>
            <p className="text-[#73706A] text-sm mt-1">
              Antecipa a cobrança automática do Stripe para o <strong>dia 30</strong> (em vez do dia 1º),
              para o dinheiro cair na conta no início do mês.
            </p>
          </div>
          <Button variant="outline" onClick={fetchPreview} disabled={loading} data-testid="refresh-subs-btn">
            <ArrowsClockwise size={16} className="mr-2" /> Atualizar
          </Button>
        </div>

        <div className="bg-[#FFF7ED] border border-[#FED7AA] rounded-xl p-4 flex gap-3">
          <Warning size={20} className="text-[#E85D04] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[#7C2D12]">
            <p className="font-semibold">Cobrança real (Stripe LIVE)</p>
            <p className="mt-1">
              Ao aplicar, cada assinatura passa a cobrar no dia 30. A próxima cobrança acontece em{' '}
              <strong>{data?.target_date || '—'}</strong>. Sem cobrança extra ou proporcional. Revise a lista antes de confirmar.
            </p>
          </div>
        </div>

        {result && (
          <div className="bg-[#F0FDF4] border border-[#BBF7D0] rounded-xl p-4 flex gap-3" data-testid="apply-result">
            <CheckCircle size={20} className="text-[#16A34A] flex-shrink-0 mt-0.5" />
            <div className="text-sm text-[#14532D]">
              <p className="font-semibold">Aplicado: {result.updated} atualizada(s), {result.errors} erro(s)</p>
              <p className="mt-1">Nova data de cobrança: <strong>{result.target_date}</strong></p>
            </div>
          </div>
        )}

        <div className="bg-white border border-[#E6E2D8] rounded-xl overflow-hidden">
          <div className="px-5 py-3 border-b border-[#E6E2D8] flex items-center justify-between">
            <span className="font-semibold text-[#1A1A1A]">
              {loading ? 'Carregando...' : `${subs.length} assinatura(s) ativa(s)`}
            </span>
            <Button
              onClick={() => setConfirmOpen(true)}
              disabled={loading || subs.length === 0}
              className="bg-[#E85D04] hover:bg-[#DC2F02] text-white"
              data-testid="apply-billing-day-btn"
            >
              Aplicar dia 30 a todas
            </Button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAF8F3] text-[#73706A]">
                <tr>
                  <th className="text-left px-5 py-3 font-medium">Aluno</th>
                  <th className="text-left px-5 py-3 font-medium">E-mail</th>
                  <th className="text-right px-5 py-3 font-medium">Valor</th>
                  <th className="text-center px-5 py-3 font-medium">Cobrança atual</th>
                  <th className="text-center px-5 py-3 font-medium">Nova cobrança</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s, i) => (
                  <tr key={s.id} className="border-t border-[#F0EDE4]" data-testid={`sub-row-${i}`}>
                    <td className="px-5 py-3 text-[#1A1A1A] font-medium">{s.name || '—'}</td>
                    <td className="px-5 py-3 text-[#73706A]">{s.email || '—'}</td>
                    <td className="px-5 py-3 text-right text-[#1A1A1A]">${s.amount?.toFixed(2)}</td>
                    <td className="px-5 py-3 text-center text-[#9B2226]">{s.current_next_charge || '—'}</td>
                    <td className="px-5 py-3 text-center text-[#2D6A4F] font-semibold">{s.proposed_next_charge || '—'}</td>
                  </tr>
                ))}
                {!loading && subs.length === 0 && (
                  <tr><td colSpan={5} className="px-5 py-8 text-center text-[#73706A]">Nenhuma assinatura ativa encontrada.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="confirm-apply-dialog">
          <DialogHeader>
            <DialogTitle>Confirmar mudança do dia de cobrança</DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#3F3F46] space-y-2">
            <p>
              Você vai mudar <strong>{subs.length} assinatura(s)</strong> para cobrar no <strong>dia 30</strong>.
            </p>
            <p>
              A próxima cobrança de cada uma acontecerá em <strong>{data?.target_date}</strong> (Stripe LIVE, cobrança real).
            </p>
            <p className="text-[#9B2226]">Esta ação não pode ser desfeita automaticamente.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={applying}>Cancelar</Button>
            <Button
              onClick={applyBillingDay}
              disabled={applying}
              className="bg-[#E85D04] hover:bg-[#DC2F02] text-white"
              data-testid="confirm-apply-btn"
            >
              {applying ? 'Aplicando...' : 'Sim, aplicar dia 30'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
